import { useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { PROCESS_BACKGROUND_REMOVAL } from '../lib/apollo/mutations/processBackgroundRemoval'
import { canSelectTalentCardType } from '../lib/helpers/talentCardHelpers'

interface Participant {
  id: string
  image?: string | null
  imageWithBackground?: string | null
}

interface Camp {
  end?: string | null
}

interface UseImageFieldNormalizationOptions {
  enabled?: boolean
  camp?: Camp | null
  onNormalizationStart?: (participantId: string, type: 'process' | 'migrate') => void
  onNormalizationComplete?: (participantId: string, result: any) => void
  onNormalizationError?: (participantId: string, error: any) => void
}

/**
 * Hook that automatically normalizes participant image fields
 * Handles two cases:
 * 1. If only imageWithBackground exists -> trigger background removal to generate image field
 * 2. If only image exists (legacy with background) -> move to imageWithBackground and process
 */
export const useImageFieldNormalization = (
  participant: Participant | null | undefined,
  options: UseImageFieldNormalizationOptions = {}
) => {
  const {
    enabled = true,
    camp,
    onNormalizationStart,
    onNormalizationComplete,
    onNormalizationError
  } = options

  const [processBackgroundRemoval] = useMutation(PROCESS_BACKGROUND_REMOVAL, {
    refetchQueries: ['GetCertificate', 'GetParticipant'],
    awaitRefetchQueries: true
  })

  const normalizedParticipants = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!enabled || !participant?.id) return

    // Skip if camp ends before Sept 1, 2025
    if (camp && !canSelectTalentCardType(camp.end)) {
      console.log('⏭️ Skipping image normalization for participant in old camp:', {
        participantId: participant.id,
        campEnd: camp.end
      })
      return
    }

    // Skip if already normalized
    if (normalizedParticipants.current.has(participant.id)) return

    const hasImage = participant.image && !participant.image.includes('default_talentcard.png')
    const hasImageWithBackground = participant.imageWithBackground && !participant.imageWithBackground.includes('default_talentcard.png')

    // Case 1: Only imageWithBackground exists (migration case - needs processing)
    if (!hasImage && hasImageWithBackground) {
      console.log('🔄 Migration case: Participant has only imageWithBackground, generating processed image')

      normalizedParticipants.current.add(participant.id)
      onNormalizationStart?.(participant.id, 'process')

      processBackgroundRemoval({
        variables: { participantId: participant.id }
      })
        .then(result => {
          console.log('✅ Background removal completed (migration case):', result)
          onNormalizationComplete?.(participant.id, result)
        })
        .catch(error => {
          console.error('❌ Background removal failed (migration case):', error)
          // Remove from normalized set so it can be retried
          normalizedParticipants.current.delete(participant.id)
          onNormalizationError?.(participant.id, error)
        })
    }

    // Case 2: Only image exists (legacy case - needs migration)
    if (hasImage && !hasImageWithBackground) {
      console.log('🔄 Legacy case: Participant has only image field (contains background), moving to imageWithBackground and processing')

      normalizedParticipants.current.add(participant.id)
      onNormalizationStart?.(participant.id, 'migrate')

      processBackgroundRemoval({
        variables: { participantId: participant.id }
      })
        .then(result => {
          console.log('✅ Background removal completed (legacy case):', result)
          onNormalizationComplete?.(participant.id, result)
        })
        .catch(error => {
          console.error('❌ Background removal failed (legacy case):', error)
          // Remove from normalized set so it can be retried
          normalizedParticipants.current.delete(participant.id)
          onNormalizationError?.(participant.id, error)
        })
    }

    // Both fields exist - already normalized
    if (hasImage && hasImageWithBackground) {
      console.log('✅ Participant already normalized with both image fields')
    }
  }, [enabled, participant, camp, processBackgroundRemoval, onNormalizationStart, onNormalizationComplete, onNormalizationError])

  return {
    isNormalizing: false // Could track this with state if needed
  }
}
